import { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent } from "./ui/sheet";
import { PriorityBadge, StatusBadge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input, Select } from "./ui/input";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { fmtDate } from "@/lib/quarter";
import { api } from "@/lib/api";
import type { AuditEntry, Initiative, InitiativeLink, LinkType } from "@/lib/types";
import { format, parseISO } from "date-fns";

interface Props {
  initiative: Initiative | null;
  isAdmin: boolean;
  allInitiatives: Initiative[];
  links: InitiativeLink[];
  onClose: () => void;
  onEdit: (i: Initiative) => void;
  onDelete: (i: Initiative) => Promise<void>;
  onLinksChanged: () => Promise<void>;
}

export function InitiativeDetailPanel({
  initiative,
  isAdmin,
  allInitiatives,
  links,
  onClose,
  onEdit,
  onDelete,
  onLinksChanged,
}: Props) {
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!initiative) return;
    setLoading(true);
    api
      .audit(initiative.id)
      .then(setAudit)
      .catch(() => setAudit([]))
      .finally(() => setLoading(false));
  }, [initiative]);

  const byId = useMemo(() => {
    const m = new Map<number, Initiative>();
    for (const i of allInitiatives) m.set(i.id, i);
    return m;
  }, [allInitiatives]);

  const partition = useMemo(() => {
    if (!initiative)
      return {
        blocking: [] as InitiativeLink[],
        blockedBy: [] as InitiativeLink[],
        related: [] as InitiativeLink[],
      };
    return {
      blocking: links.filter((l) => l.from_id === initiative.id && l.type === "blocks"),
      blockedBy: links.filter((l) => l.to_id === initiative.id && l.type === "blocks"),
      related: links.filter(
        (l) => l.type === "related_to" && (l.from_id === initiative.id || l.to_id === initiative.id),
      ),
    };
  }, [initiative, links]);

  if (!initiative) return null;

  return (
    <Sheet open={!!initiative} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="p-0">
        <div className="p-6 border-b">
          <div className="flex items-start gap-2 mb-3">
            <span
              className="mt-1 h-3 w-3 rounded-sm shrink-0"
              style={{ backgroundColor: initiative.team_color }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-muted-foreground mb-1">
                {initiative.team_name} · {initiative.product_name}
              </div>
              <h2 className="text-xl font-semibold leading-tight pr-8">{initiative.title}</h2>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <StatusBadge status={initiative.status} />
            <PriorityBadge priority={initiative.priority} />
            <span className="text-xs px-2 py-0.5 rounded-md border bg-secondary/40 text-muted-foreground">
              {initiative.quarter}
            </span>
          </div>

          {initiative.description && (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
              {initiative.description}
            </p>
          )}

          <dl className="mt-5 grid grid-cols-2 gap-3 text-sm">
            <Row label="Start">{fmtDate(initiative.start_date)}</Row>
            <Row label="End">{fmtDate(initiative.end_date)}</Row>
            <Row label="Owner">{initiative.owner ?? "—"}</Row>
            <Row label="Jira">
              {initiative.jira_key ? (
                <span className="font-mono text-xs">{initiative.jira_key}</span>
              ) : (
                "—"
              )}
            </Row>
          </dl>

          {isAdmin && (
            <div className="flex gap-2 mt-5">
              <Button size="sm" variant="secondary" onClick={() => onEdit(initiative)}>
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:bg-destructive/10"
                onClick={() => {
                  if (confirm("Delete this initiative?")) onDelete(initiative);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </Button>
            </div>
          )}
        </div>

        <DependenciesSection
          initiative={initiative}
          isAdmin={isAdmin}
          allInitiatives={allInitiatives}
          byId={byId}
          partition={partition}
          onChange={onLinksChanged}
        />

        <div className="p-6 border-t">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
            Activity
          </h3>
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : audit.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">No activity yet.</div>
          ) : (
            <ol className="relative border-l border-border pl-4 space-y-3">
              {audit.map((e) => (
                <li key={e.id} className="relative">
                  <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary ring-4 ring-card" />
                  <div className="text-xs text-muted-foreground">
                    {format(parseISO(e.created_at.replace(" ", "T") + "Z"), "MMM d, HH:mm")} ·{" "}
                    <span className="text-foreground">{e.actor}</span>
                  </div>
                  <div className="text-sm">
                    {e.action === "created" && "Created the initiative"}
                    {e.action === "deleted" && "Deleted the initiative"}
                    {e.action === "updated" && (
                      <>
                        Changed <code className="text-xs bg-secondary px-1 rounded">{e.field}</code>
                        {e.old_value != null && e.new_value != null && (
                          <>
                            {" "}
                            <span className="text-muted-foreground line-through">
                              {truncate(e.old_value)}
                            </span>{" "}
                            → <span>{truncate(e.new_value)}</span>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DependenciesSection({
  initiative,
  isAdmin,
  allInitiatives,
  byId,
  partition,
  onChange,
}: {
  initiative: Initiative;
  isAdmin: boolean;
  allInitiatives: Initiative[];
  byId: Map<number, Initiative>;
  partition: {
    blocking: InitiativeLink[];
    blockedBy: InitiativeLink[];
    related: InitiativeLink[];
  };
  onChange: () => Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  const [direction, setDirection] = useState<"blocks-out" | "blocks-in" | "related">("blocks-out");
  const [targetId, setTargetId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const candidates = allInitiatives.filter((i) => i.id !== initiative.id);

  const totalLinks =
    partition.blocking.length + partition.blockedBy.length + partition.related.length;

  async function add() {
    if (!targetId) return;
    setBusy(true);
    setErr(null);
    try {
      const tid = Number(targetId);
      if (direction === "blocks-out")
        await api.createLink(initiative.id, tid, "blocks");
      else if (direction === "blocks-in")
        await api.createLink(tid, initiative.id, "blocks");
      else await api.createLink(initiative.id, tid, "related_to");
      await onChange();
      setAdding(false);
      setTargetId("");
    } catch (e: any) {
      setErr(e?.message ?? "failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove(linkId: number) {
    setBusy(true);
    try {
      await api.deleteLink(linkId);
      await onChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="px-6 py-5 border-t">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Dependencies
          {totalLinks > 0 && (
            <span className="ml-2 text-foreground font-medium normal-case tracking-normal">
              ({totalLinks})
            </span>
          )}
        </h3>
        {isAdmin && !adding && (
          <Button size="sm" variant="ghost" onClick={() => setAdding(true)}>
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        )}
      </div>

      {adding && (
        <div className="rounded-md border bg-secondary/30 p-3 mb-4 grid gap-2">
          <div className="grid grid-cols-2 gap-2">
            <Select
              value={direction}
              onChange={(e) => setDirection(e.target.value as typeof direction)}
            >
              <option value="blocks-out">This blocks…</option>
              <option value="blocks-in">…blocks this</option>
              <option value="related">Related to</option>
            </Select>
            <Select value={targetId} onChange={(e) => setTargetId(e.target.value)}>
              <option value="">Choose initiative…</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </Select>
          </div>
          {err && <div className="text-xs text-destructive">{err}</div>}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={!targetId || busy} onClick={add}>
              Add link
            </Button>
          </div>
        </div>
      )}

      <DepGroup label="Blocks" empty="—" items={partition.blocking} highlight>
        {partition.blocking.map((l) => (
          <DepRow
            key={l.id}
            target={byId.get(l.to_id)}
            isAdmin={isAdmin}
            onRemove={() => remove(l.id)}
          />
        ))}
      </DepGroup>

      <DepGroup label="Blocked by" empty="—" items={partition.blockedBy} highlight>
        {partition.blockedBy.map((l) => (
          <DepRow
            key={l.id}
            target={byId.get(l.from_id)}
            isAdmin={isAdmin}
            onRemove={() => remove(l.id)}
          />
        ))}
      </DepGroup>

      <DepGroup label="Related to" empty="—" items={partition.related}>
        {partition.related.map((l) => {
          const otherId = l.from_id === initiative.id ? l.to_id : l.from_id;
          return (
            <DepRow
              key={l.id}
              target={byId.get(otherId)}
              isAdmin={isAdmin}
              onRemove={() => remove(l.id)}
            />
          );
        })}
      </DepGroup>
    </div>
  );
}

function DepGroup({
  label,
  empty,
  items,
  highlight,
  children,
}: {
  label: string;
  empty: string;
  items: unknown[];
  highlight?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">
        {label}
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-muted-foreground/60 italic ml-1">{empty}</div>
      ) : (
        <div className="grid gap-1.5">{children}</div>
      )}
    </div>
  );
}

function DepRow({
  target,
  isAdmin,
  onRemove,
}: {
  target: Initiative | undefined;
  isAdmin: boolean;
  onRemove: () => void;
}) {
  if (!target) return null;
  return (
    <div
      className="flex items-center gap-2 rounded-md border bg-secondary/30 px-2.5 py-1.5 group"
    >
      <span
        className="h-2 w-2 rounded-sm shrink-0"
        style={{ backgroundColor: target.team_color }}
      />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate">{target.title}</div>
        <div className="text-[10px] text-muted-foreground truncate">
          {target.team_name} · {target.quarter}
        </div>
      </div>
      {isAdmin && (
        <button
          onClick={onRemove}
          className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity p-1"
          title="Remove link"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}

function truncate(s: string, max = 60): string {
  return s.length > max ? s.slice(0, max - 1) + "…" : s;
}
